import { ChangeDetectionStrategy, Component } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { Select, ofActionSuccessful } from "@ngxs/store";
import { AppDataState } from "@app/shared/stores/app-data.state";
import { NgxsActionName, NgxsFormPath } from "@app/shared/constants";
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from "rxjs";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { DataGridComponent } from "@app/shared/components/smart-data-grid/data-grid/data-grid.component";
import { DataGridColumnDef, GetOrgUnitSearchByList } from "./org-unit.constant";
import { SearchByItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { SmartSearchFormComponent } from "@app/shared/components/smart-search-form/smart-search-form.component";
import { GetOrgUnit } from "@app/shared/actions/app-data.action";
import { OrgUnit } from "@app/shared/classes/org-unit";
import { OrgUnitEditModalDialogComponent } from "@app/shared/components/edit-modal-dialog/org-unit-edit-modal-dialog/org-unit-edit-modal-dialog.component";
import * as AppDataAction from "@app/shared/actions/app-data.action";
import { CommonModalDialogComponent } from "@app/shared/components/common-modal-dialog/common-modal-dialog.component";
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from "@app/shared/components/common-modal-dialog/common-modal-dialog.constant";
import { MessageDataComponent } from "@app/shared/components/message-data/message-data.component";
import { CommonPageFilterSetting, CommonSearchCriteria } from "@app/shared/models/common-data.model";

@Component({
  selector: "app-org-unit",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, CommonModule, DataGridComponent, SmartSearchFormComponent, MessageDataComponent],
  templateUrl: "./org-unit.component.html",
  styleUrl: "./org-unit.component.css",
})
export class OrgUnitComponent extends PageBasedComponent {
  selectedItems: OrgUnit[] = [];
  formPath = NgxsFormPath.ORG_UNIT_SEARCH_FORM;
  dataGridColumnDef = DataGridColumnDef;
  dataSource$: Observable<OrgUnit[]>;
  orgUnitSearchByList$: Observable<SearchByItem[]>;
  unAssignedTerminalError$: Observable<string[]>;

  @Select(AppDataState.currentOrgUnitSearchBy) currentOrgUnitSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_ORG_UNIT)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_ORG_UNIT)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_ORG_UNIT)) response$: Observable<WebApiResponse<OrgUnit[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_ORG_UNIT)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_ORG_UNIT)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_ORG_UNIT;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentOrgUnitSearchBy$.pipe(take(1)).subscribe((searchCriteria) => this.defaultSearchCriteriaSub.next(searchCriteria));

    this.unAssignedTerminalError$ = this.unMappingTerminals$.pipe(map((t) => (t && t.length > 0 ? ["ml_152080", t] : [])));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map((errorResponse) => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.data)
    );
    this.totalCount$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.totalCount)
    );

    this.orgUnitSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map((c) => GetOrgUnitSearchByList(c)));

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.DeleteOrgUnit), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$))
      .subscribe(([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch([new GetOrgUnit(), new AppDataAction.GetTerminalsWithoutSubregion()]);
  };

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch([new GetOrgUnit(null), new AppDataAction.GetTerminalsWithoutSubregion()]);
  };

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_ORG_UNIT;
    this.store.dispatch(new GetOrgUnit(info));
  };

  onCreate = () => {
    this.dialog.open(OrgUnitEditModalDialogComponent, {
      width: "700px",
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true,
    });
  };

  rowSelected = (data: OrgUnit[]) => {
    this.selectedItems = data ?? [];
  };

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.data !== ModalDialogResult.YES) {
          return;
        }

        this.store.dispatch(new AppDataAction.DeleteOrgUnit(this.selectedItems.map((item) => item.terminal_Id)));
      });
  };
}
